import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlusOne as PlusOneIcon,
} from '@mui/icons-material';
import { format, addDays, startOfDay } from 'date-fns';
import { ja } from 'date-fns/locale';
import { Shift } from '../../types/shift';
import { ShiftRequest } from '../../types/shift-request';
import { Employee } from '../../types/employee';

interface DailyViewProps {
  shifts: Shift[];
  shiftRequests: ShiftRequest[];
  employees: Employee[];
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  onShiftClick: (shift: Shift) => void;
  onAddShift: (employeeId: string, startTime: string, endTime: string) => void;
  onUpdateShift?: (shiftId: string, startTime: string, endTime: string, employeeId?: string) => void;
  onUpdateRequest?: (requestId: string, startTime: string, endTime: string) => void;
  onDeleteRequest?: (requestId: string) => void;
  onIncreaseRequest?: (requestId: string) => void;
  /**
   * 営業日の区切り時刻（0-23）。ガントの左端になる。
   * 以前は 6 を直書きしていたため、会計（店舗設定）や NightPro（5固定）と
   * 日付の切れ目が食い違い、同じ一夜が別々の日に分かれて表示されていた。
   */
  boundaryHour?: number;
}

const CELL_WIDTH = 50; // px per hour (幅を狭くして全体を表示)
const ROW_HEIGHT = 50;
const HOUR_WIDTH = 80;

export default function DailyView({
  shifts,
  shiftRequests,
  employees,
  selectedDate,
  onDateChange,
  onShiftClick,
  onAddShift,
  onUpdateShift,
  onUpdateRequest,
  onDeleteRequest,
  onIncreaseRequest,
  boundaryHour,
}: DailyViewProps) {
  // 営業日の区切り時刻。0（未設定）の店は従来どおり 6:00 起点で描く
  // （0:00 起点にすると 20:00-翌1:00 のシフトが画面の左右端に割れるため）。
  const ganttStart = boundaryHour && boundaryHour > 0 ? boundaryHour : 6;
  const HOURS = Array.from({ length: 24 }, (_, i) => (i + ganttStart) % 24);
  const [dragStart, setDragStart] = useState<{ employeeId: string; hour: number } | null>(null);
  const [resizing, setResizing] = useState<{ shiftId: string; side: 'left' | 'right'; startX: number; startY: number; originalStart: string; originalEnd: string; employeeId: string } | null>(null);
  const [draggingShift, setDraggingShift] = useState<{ shiftId: string; startX: number; startY: number; originalStart: string; originalEnd: string; originalEmployeeId: string } | null>(null);
  const [resizingRequest, setResizingRequest] = useState<{ requestId: string; virtualId: string; side: 'left' | 'right'; startX: number; originalStart: string; originalEnd: string } | null>(null);
  const [draggingRequest, setDraggingRequest] = useState<{ requestId: string; virtualId: string; startX: number; startY: number; originalStart: string; originalEnd: string } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragPreview, setDragPreview] = useState<{ employeeId: string; startTime: string; endTime: string } | null>(null);
  const [requestDragPreview, setRequestDragPreview] = useState<{ requestId: string; virtualId: string; startTime: string; endTime: string } | null>(null);
  const [contextMenu, setContextMenu] = useState<{ shiftId: string; mouseX: number; mouseY: number } | null>(null);
  const [requestContextMenu, setRequestContextMenu] = useState<{ requestId: string; mouseX: number; mouseY: number } | null>(null);

  // dragPreviewの最新値を保持するref
  const dragPreviewRef = useRef(dragPreview);
  const requestDragPreviewRef = useRef(requestDragPreview);
  useEffect(() => {
    dragPreviewRef.current = dragPreview;
  }, [dragPreview]);
  useEffect(() => {
    requestDragPreviewRef.current = requestDragPreview;
  }, [requestDragPreview]);

  // 選択日のシフトをフィルタリング
  const dailyShifts = shifts.filter((shift) => {
    const shiftDate = startOfDay(shift.shiftDate);
    const selectedDayStart = startOfDay(selectedDate);
    return shiftDate.getTime() === selectedDayStart.getTime();
  });

  // 選択日の募集をフィルタリング
  const dailyRequests = shiftRequests.filter((request) => {
    if (!request.newShift) return false;
    const requestDate = startOfDay(request.newShift.shiftDate);
    const selectedDayStart = startOfDay(selectedDate);
    return requestDate.getTime() === selectedDayStart.getTime();
  });

  // 募集を仮想従業員として展開（必要人数分）
  const virtualRequestEmployees = dailyRequests.flatMap((request, reqIndex) => {
    if (!request.newShift) return [];
    const newShift = request.newShift; // TypeScript型チェック用
    const count = newShift.requiredStaffCount || 1;
    return Array.from({ length: count }, (_, i) => ({
      id: `request-${request.id}-${i}`,
      requestId: request.id,
      name: `募集${reqIndex + 1}${count > 1 ? `-${i + 1}` : ''}`,
      position: newShift.position || '',
      startTime: newShift.startTime,
      endTime: newShift.endTime,
    }));
  });

  // 各時間帯の人数を計算 (6時未満は翌日として扱う)
  const getStaffCountAtHour = (hour: number): number => {
    return dailyShifts.filter((shift) => {
      const startHour = parseInt(shift.startTime.split(':')[0]);
      const endHour = parseInt(shift.endTime.split(':')[0]);

      // 開始・終了時刻を区切り時刻基準に変換（区切り前の時刻は翌日扱い）
      const adjustedStart = startHour < ganttStart ? startHour + 24 : startHour;
      const adjustedEnd = endHour < ganttStart ? endHour + 24 : endHour;
      const adjustedHour = hour < ganttStart ? hour + 24 : hour;

      return adjustedHour >= adjustedStart && adjustedHour < adjustedEnd;
    }).length;
  };

  // スタッフのシフトを取得
  const getEmployeeShifts = (employeeId: string): Shift[] => {
    return dailyShifts.filter((shift) => shift.employeeId === employeeId);
  };

  // 時間からピクセル位置を計算 (6:00を開始点として、翌日の時間も考慮)
  const timeToPixel = (time: string): number => {
    const [hour, minute] = time.split(':').map(Number);
    // 区切り時刻より前は翌日とみなす
    const adjustedHour = hour < ganttStart ? hour + 24 : hour;
    return ((adjustedHour - ganttStart) * CELL_WIDTH) + ((minute / 60) * CELL_WIDTH);
  };

  // ピクセルから時間への変換
  const pixelToTime = (pixel: number): string => {
    const totalMinutes = Math.round((pixel / CELL_WIDTH) * 60);
    const hours = Math.floor(totalMinutes / 60) + ganttStart;
    const minutes = totalMinutes % 60;
    const adjustedHours = hours >= 24 ? hours - 24 : hours;
    return `${adjustedHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // リサイズハンドル用のマウスダウン
  const handleResizeMouseDown = (e: React.MouseEvent, shift: Shift, side: 'left' | 'right') => {
    e.stopPropagation();
    e.preventDefault();
    console.log('Resize mousedown:', side, shift.id);
    setIsDragging(false);
    setResizing({
      shiftId: shift.id,
      side,
      startX: e.clientX,
      startY: e.clientY,
      originalStart: shift.startTime,
      originalEnd: shift.endTime,
      employeeId: shift.employeeId,
    });
  };

  // シフト全体のドラッグ開始
  const handleShiftDragStart = (e: React.MouseEvent, shift: Shift) => {
    // クリックがリサイズハンドル上でない場合のみ
    const target = e.target as HTMLElement;
    if (target.classList.contains('resize-handle')) {
      console.log('Clicked on resize handle, skipping drag');
      return;
    }

    console.log('Shift drag start:', shift.id, 'at', e.clientX, e.clientY);
    setIsDragging(false);
    setDraggingShift({
      shiftId: shift.id,
      startX: e.clientX,
      startY: e.clientY,
      originalStart: shift.startTime,
      originalEnd: shift.endTime,
      originalEmployeeId: shift.employeeId,
    });
  };

  // マウス移動中の処理
  useEffect(() => {
    if (!resizing && !draggingShift && !resizingRequest && !draggingRequest) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (resizingRequest && onUpdateRequest) {
        // 募集枠のリサイズ処理
        const deltaX = e.clientX - resizingRequest.startX;

        const startPixel = timeToPixel(resizingRequest.originalStart);
        const endPixel = timeToPixel(resizingRequest.originalEnd);

        let newStartPixel = startPixel;
        let newEndPixel = endPixel;

        if (resizingRequest.side === 'left') {
          newStartPixel = Math.max(0, startPixel + deltaX);
          newStartPixel = Math.min(newStartPixel, endPixel - CELL_WIDTH * 0.5); // 最低30分
        } else {
          newEndPixel = Math.max(startPixel + CELL_WIDTH * 0.5, endPixel + deltaX); // 最低30分
          newEndPixel = Math.min(newEndPixel, HOURS.length * CELL_WIDTH);
        }

        const newStartTime = pixelToTime(newStartPixel);
        const newEndTime = pixelToTime(newEndPixel);

        setRequestDragPreview({
          requestId: resizingRequest.requestId,
          virtualId: resizingRequest.virtualId,
          startTime: newStartTime,
          endTime: newEndTime,
        });
      } else if (draggingRequest && onUpdateRequest) {
        // 募集枠のドラッグ処理
        const deltaX = e.clientX - draggingRequest.startX;
        const deltaY = e.clientY - draggingRequest.startY;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        if (distance > 5 && !isDragging) {
          setIsDragging(true);
        }

        const startPixel = timeToPixel(draggingRequest.originalStart);
        const endPixel = timeToPixel(draggingRequest.originalEnd);
        const duration = endPixel - startPixel;

        let newStartPixel = Math.max(0, startPixel + deltaX);
        newStartPixel = Math.min(newStartPixel, HOURS.length * CELL_WIDTH - duration);

        const newEndPixel = newStartPixel + duration;

        const newStartTime = pixelToTime(newStartPixel);
        const newEndTime = pixelToTime(newEndPixel);

        setRequestDragPreview({
          requestId: draggingRequest.requestId,
          virtualId: draggingRequest.virtualId,
          startTime: newStartTime,
          endTime: newEndTime,
        });
      } else if (resizing && onUpdateShift) {
        // マウスが5px以上動いたらドラッグと判定
        const deltaX = e.clientX - resizing.startX;
        const deltaY = e.clientY - resizing.startY;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        if (distance > 5 && !isDragging) {
          console.log('Resizing detected, distance:', distance);
          setIsDragging(true);
        }

        const startPixel = timeToPixel(resizing.originalStart);
        const endPixel = timeToPixel(resizing.originalEnd);

        let newStartPixel = startPixel;
        let newEndPixel = endPixel;

        if (resizing.side === 'left') {
          newStartPixel = Math.max(0, startPixel + deltaX);
          newStartPixel = Math.min(newStartPixel, endPixel - CELL_WIDTH * 0.5); // 最低30分
        } else {
          newEndPixel = Math.max(startPixel + CELL_WIDTH * 0.5, endPixel + deltaX); // 最低30分
          newEndPixel = Math.min(newEndPixel, HOURS.length * CELL_WIDTH);
        }

        const newStartTime = pixelToTime(newStartPixel);
        const newEndTime = pixelToTime(newEndPixel);

        // プレビュー表示を更新（実際のFirestore更新はマウスアップ時）
        console.log('Setting dragPreview for resize:', resizing.employeeId, newStartTime, newEndTime);
        setDragPreview({
          employeeId: resizing.employeeId,
          startTime: newStartTime,
          endTime: newEndTime,
        });
      } else if (draggingShift && onUpdateShift) {
        // マウスが5px以上動いたらドラッグと判定
        const deltaX = e.clientX - draggingShift.startX;
        const deltaY = e.clientY - draggingShift.startY;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        if (distance > 5 && !isDragging) {
          console.log('Dragging shift detected, distance:', distance);
          setIsDragging(true);
        }

        const startPixel = timeToPixel(draggingShift.originalStart);
        const endPixel = timeToPixel(draggingShift.originalEnd);
        const duration = endPixel - startPixel;

        let newStartPixel = Math.max(0, startPixel + deltaX);
        newStartPixel = Math.min(newStartPixel, HOURS.length * CELL_WIDTH - duration);

        const newEndPixel = newStartPixel + duration;

        const newStartTime = pixelToTime(newStartPixel);
        const newEndTime = pixelToTime(newEndPixel);

        // 垂直方向の移動を検出してスタッフ行を特定
        const ganttElement = document.querySelector('.gantt-rows');
        console.log('ganttElement:', ganttElement, 'employees.length:', employees.length);
        if (ganttElement) {
          const ganttRect = ganttElement.getBoundingClientRect();
          const relativeY = e.clientY - ganttRect.top;
          const rowIndex = Math.floor(relativeY / ROW_HEIGHT);
          console.log('rowIndex:', rowIndex, 'mouseY:', e.clientY);

          if (rowIndex >= 0 && rowIndex < employees.length) {
            const newEmployeeId = employees[rowIndex].id;
            console.log('Setting dragPreview for employee:', newEmployeeId);

            // プレビュー表示を更新
            setDragPreview({
              employeeId: newEmployeeId,
              startTime: newStartTime,
              endTime: newEndTime,
            });
          } else {
            console.log('rowIndex out of bounds:', rowIndex, 'employees.length:', employees.length);
          }
        } else {
          console.log('ganttElement not found');
        }
      }
    };

    const handleMouseUp = () => {
      const currentDragPreview = dragPreviewRef.current;
      const currentRequestDragPreview = requestDragPreviewRef.current;
      console.log('Mouse up, dragPreview:', currentDragPreview, 'requestDragPreview:', currentRequestDragPreview);

      // 募集枠のリサイズまたはドラッグ終了時
      if (currentRequestDragPreview && onUpdateRequest) {
        if (resizingRequest || draggingRequest) {
          console.log('Updating request:', currentRequestDragPreview.requestId, currentRequestDragPreview.startTime, currentRequestDragPreview.endTime);
          onUpdateRequest(
            currentRequestDragPreview.requestId,
            currentRequestDragPreview.startTime,
            currentRequestDragPreview.endTime
          );
        }
      }

      // ドラッグまたはリサイズ終了時に最終的な更新を実行
      if (currentDragPreview && onUpdateShift) {
        if (draggingShift) {
          // ドラッグの場合
          const newEmployeeId = currentDragPreview.employeeId !== draggingShift.originalEmployeeId
            ? currentDragPreview.employeeId
            : undefined;

          console.log('Updating shift drag:', draggingShift.shiftId, currentDragPreview.startTime, currentDragPreview.endTime, newEmployeeId);
          onUpdateShift(
            draggingShift.shiftId,
            currentDragPreview.startTime,
            currentDragPreview.endTime,
            newEmployeeId
          );
        } else if (resizing) {
          // リサイズの場合
          console.log('Updating shift resize:', resizing.shiftId, currentDragPreview.startTime, currentDragPreview.endTime);
          onUpdateShift(
            resizing.shiftId,
            currentDragPreview.startTime,
            currentDragPreview.endTime
          );
        }
      }

      setResizing(null);
      setDraggingShift(null);
      setDragPreview(null);
      setResizingRequest(null);
      setDraggingRequest(null);
      setRequestDragPreview(null);
      // 少し遅延してからisDraggingをリセット（クリックイベントの後に）
      setTimeout(() => setIsDragging(false), 100);
    };

    console.log('Adding event listeners, resizing:', !!resizing, 'draggingShift:', !!draggingShift, 'resizingRequest:', !!resizingRequest, 'draggingRequest:', !!draggingRequest);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      console.log('Removing event listeners');
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resizing, draggingShift, resizingRequest, draggingRequest]);

  // シフトバーの描画
  const renderShiftBar = (shift: Shift, isGhost: boolean = false) => {
    const startPixel = timeToPixel(shift.startTime);
    const endPixel = timeToPixel(shift.endTime);
    const width = endPixel - startPixel;

    const statusColor = {
      pending: '#FFA726',
      approved: '#66BB6A',
      requested: '#42A5F5',
      rejected: '#EF5350',
    }[shift.status] || '#999';

    return (
      <Box
        key={shift.id}
        onMouseDown={(e) => {
          if (e.button === 0) {
            // 左クリック：ドラッグ開始
            handleShiftDragStart(e, shift);
          }
        }}
        onContextMenu={(e) => {
          // 右クリック：コンテキストメニュー表示
          e.preventDefault();
          setContextMenu({
            shiftId: shift.id,
            mouseX: e.clientX - 2,
            mouseY: e.clientY - 4,
          });
        }}
        onDoubleClick={(e) => {
          // ダブルクリック：詳細編集
          if (!isDragging) {
            onShiftClick(shift);
          }
        }}
        sx={{
          position: 'absolute',
          left: startPixel,
          width: width,
          height: ROW_HEIGHT - 10,
          bgcolor: statusColor,
          color: 'white',
          borderRadius: 1,
          cursor: draggingShift?.shiftId === shift.id ? 'grabbing' : 'grab',
          display: 'flex',
          opacity: isGhost ? 0.3 : 1,
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.75rem',
          fontWeight: 'bold',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          px: 1,
          '&:hover': {
            opacity: 0.8,
            boxShadow: 2,
          },
        }}
      >
        {/* 左リサイズハンドル */}
        <Box
          className="resize-handle"
          onMouseDown={(e) => handleResizeMouseDown(e, shift, 'left')}
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 8,
            cursor: 'ew-resize',
            bgcolor: 'rgba(0,0,0,0.2)',
            '&:hover': {
              bgcolor: 'rgba(0,0,0,0.4)',
            },
          }}
        />

        {/* シフト時間表示 */}
        {shift.startTime} - {shift.endTime}

        {/* 右リサイズハンドル */}
        <Box
          className="resize-handle"
          onMouseDown={(e) => handleResizeMouseDown(e, shift, 'right')}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: 8,
            cursor: 'ew-resize',
            bgcolor: 'rgba(0,0,0,0.2)',
            '&:hover': {
              bgcolor: 'rgba(0,0,0,0.4)',
            },
          }}
        />
      </Box>
    );
  };

  // セルのマウスダウン開始（新規シフト作成用）
  const handleCellMouseDown = (employeeId: string, hour: number) => {
    setDragStart({ employeeId, hour });
  };

  // セルのマウスアップ終了（新規シフト作成用）
  const handleCellMouseUp = (hour: number) => {
    if (dragStart && dragStart.employeeId) {
      const startHour = Math.min(dragStart.hour, hour);
      const endHour = Math.max(dragStart.hour, hour) + 1;
      const startTime = `${startHour.toString().padStart(2, '0')}:00`;
      const endTime = `${endHour.toString().padStart(2, '0')}:00`;

      onAddShift(dragStart.employeeId, startTime, endTime);
      setDragStart(null);
    }
  };

  const getEmployeeName = (employee: Employee): string => {
    return `${employee.personalInfo.lastName} ${employee.personalInfo.firstName}`;
  };

  // スタッフ変更
  const handleChangeEmployee = (newEmployeeId: string) => {
    if (!contextMenu || !onUpdateShift) return;

    const shift = shifts.find((s) => s.id === contextMenu.shiftId);
    if (!shift) return;

    // employeeIdを更新
    onUpdateShift(contextMenu.shiftId, shift.startTime, shift.endTime, newEmployeeId);

    setContextMenu(null);
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  return (
    <Box>
      {/* 日付選択ヘッダー */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => onDateChange(addDays(selectedDate, -1))}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="h6">
            {format(selectedDate, 'yyyy年M月d日(E)', { locale: ja })}
          </Typography>
          <IconButton onClick={() => onDateChange(addDays(selectedDate, 1))}>
            <ChevronRightIcon />
          </IconButton>
          <Button
            size="small"
            onClick={() => onDateChange(new Date())}
            sx={{ ml: 2 }}
          >
            今日
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary">
          ドラッグしてシフトを追加、クリックで編集
        </Typography>
      </Paper>

      {/* ガントチャート */}
      <Paper sx={{ p: 2, overflowX: 'auto' }}>
        <Box sx={{ minWidth: HOUR_WIDTH + HOURS.length * CELL_WIDTH }}>
          {/* 時間軸ヘッダー */}
          <Box sx={{ display: 'flex', mb: 1 }}>
            <Box sx={{ width: HOUR_WIDTH, flexShrink: 0 }} />
            {HOURS.map((hour) => (
              <Box
                key={hour}
                sx={{
                  width: CELL_WIDTH,
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  fontWeight: 'bold',
                  borderLeft: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {hour}:00
              </Box>
            ))}
          </Box>

          {/* スタッフ行 */}
          <Box className="gantt-rows">
            {employees.map((employee) => {
              const employeeShifts = getEmployeeShifts(employee.id);
              const isDropTarget = dragPreview && dragPreview.employeeId === employee.id;

              return (
                <Box
                  key={employee.id}
                  sx={{
                    display: 'flex',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    height: ROW_HEIGHT,
                    bgcolor: isDropTarget ? 'action.hover' : 'transparent',
                  }}
                >
                  {/* スタッフ名 */}
                  <Box
                    sx={{
                      width: HOUR_WIDTH,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      px: 1,
                      fontWeight: 'bold',
                      fontSize: '0.875rem',
                      bgcolor: 'grey.50',
                    }}
                  >
                    {getEmployeeName(employee)}
                  </Box>

                  {/* タイムライン */}
                  <Box sx={{ position: 'relative', flex: 1, display: 'flex' }}>
                    {HOURS.map((hour) => (
                      <Box
                        key={hour}
                        onMouseDown={() => handleCellMouseDown(employee.id, hour)}
                        onMouseUp={() => handleCellMouseUp(hour)}
                        sx={{
                          width: CELL_WIDTH,
                          borderLeft: '1px solid',
                          borderColor: 'divider',
                          cursor: 'crosshair',
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                        }}
                      />
                    ))}

                    {/* シフトバー */}
                    {employeeShifts.map((shift) => {
                      // ドラッグ中のシフトは透明にする
                      if (draggingShift && draggingShift.shiftId === shift.id) {
                        return renderShiftBar({ ...shift, status: 'pending' as const }, true);
                      }
                      return renderShiftBar(shift);
                    })}

                    {/* ドラッグプレビュー */}
                    {isDropTarget && dragPreview && draggingShift && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: timeToPixel(dragPreview.startTime),
                          top: 5,
                          width: timeToPixel(dragPreview.endTime) - timeToPixel(dragPreview.startTime),
                          height: ROW_HEIGHT - 10,
                          bgcolor: 'primary.light',
                          opacity: 0.5,
                          border: '2px dashed',
                          borderColor: 'primary.main',
                          borderRadius: 1,
                          pointerEvents: 'none',
                        }}
                      />
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* 募集枠（仮想従業員） */}
          {virtualRequestEmployees.map((virtualEmp) => {
            return (
              <Box
                key={virtualEmp.id}
                sx={{
                  display: 'flex',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  height: ROW_HEIGHT,
                  bgcolor: 'grey.100',
                }}
              >
                {/* 募集名 */}
                <Box
                  sx={{
                    width: HOUR_WIDTH,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    px: 1,
                    fontWeight: 'bold',
                    fontSize: '0.875rem',
                    bgcolor: 'grey.200',
                    borderRight: '2px solid',
                    borderColor: 'divider',
                    color: 'warning.dark',
                  }}
                >
                  {virtualEmp.name}
                  {virtualEmp.position && ` (${virtualEmp.position})`}
                </Box>

                {/* タイムライン */}
                <Box sx={{ position: 'relative', flex: 1, display: 'flex' }}>
                  {HOURS.map((hour) => (
                    <Box
                      key={hour}
                      sx={{
                        width: CELL_WIDTH,
                        borderLeft: '1px solid',
                        borderColor: 'divider',
                      }}
                    />
                  ))}

                  {/* 募集シフトバー */}
                  {(() => {
                    // プレビュー中の時間を優先
                    const isBeingDragged = requestDragPreview?.virtualId === virtualEmp.id;
                    const displayStartTime = isBeingDragged ? requestDragPreview.startTime : virtualEmp.startTime;
                    const displayEndTime = isBeingDragged ? requestDragPreview.endTime : virtualEmp.endTime;

                    return (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: `${timeToPixel(displayStartTime)}px`,
                          width: `${timeToPixel(displayEndTime) - timeToPixel(displayStartTime)}px`,
                          height: ROW_HEIGHT - 8,
                          top: 4,
                          bgcolor: '#BDBDBD',
                          border: '2px dashed #757575',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#424242',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          cursor: 'move',
                          opacity: isBeingDragged ? 0.7 : 1,
                          '&:hover': {
                            bgcolor: '#9E9E9E',
                          },
                        }}
                        onMouseDown={(e) => {
                          // リサイズハンドルのクリックは除外
                          if ((e.target as HTMLElement).classList.contains('resize-handle')) {
                            return;
                          }

                          setDraggingRequest({
                            requestId: virtualEmp.requestId,
                            virtualId: virtualEmp.id,
                            startX: e.clientX,
                            startY: e.clientY,
                            originalStart: displayStartTime,
                            originalEnd: displayEndTime,
                          });
                        }}
                        onClick={(e) => {
                          if (!isDragging) {
                            e.preventDefault();
                            setRequestContextMenu({
                              requestId: virtualEmp.requestId,
                              mouseX: e.clientX,
                              mouseY: e.clientY,
                            });
                          }
                        }}
                      >
                        {displayStartTime} - {displayEndTime}

                    {/* 左リサイズハンドル */}
                    <Box
                      className="resize-handle"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        setResizingRequest({
                          requestId: virtualEmp.requestId,
                          virtualId: virtualEmp.id,
                          side: 'left',
                          startX: e.clientX,
                          originalStart: virtualEmp.startTime,
                          originalEnd: virtualEmp.endTime,
                        });
                      }}
                      sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 8,
                        cursor: 'ew-resize',
                        bgcolor: 'rgba(0,0,0,0.2)',
                        '&:hover': {
                          bgcolor: 'rgba(0,0,0,0.4)',
                        },
                      }}
                    />

                    {/* 右リサイズハンドル */}
                    <Box
                      className="resize-handle"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        setResizingRequest({
                          requestId: virtualEmp.requestId,
                          virtualId: virtualEmp.id,
                          side: 'right',
                          startX: e.clientX,
                          originalStart: virtualEmp.startTime,
                          originalEnd: virtualEmp.endTime,
                        });
                      }}
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: 8,
                        cursor: 'ew-resize',
                        bgcolor: 'rgba(0,0,0,0.2)',
                        '&:hover': {
                          bgcolor: 'rgba(0,0,0,0.4)',
                        },
                      }}
                    />
                      </Box>
                    );
                  })()}
                </Box>
              </Box>
            );
          })}

          {/* 人数集計行 */}
          <Box
            sx={{
              display: 'flex',
              borderTop: '2px solid',
              borderColor: 'primary.main',
              bgcolor: 'primary.light',
              height: ROW_HEIGHT,
            }}
          >
            <Box
              sx={{
                width: HOUR_WIDTH,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                px: 1,
                fontWeight: 'bold',
              }}
            >
              勤務人数
            </Box>
            {HOURS.map((hour) => (
              <Box
                key={hour}
                sx={{
                  width: CELL_WIDTH,
                  borderLeft: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                }}
              >
                {getStaffCountAtHour(hour)}人
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>

      {/* 凡例 */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 16, bgcolor: '#42A5F5', borderRadius: 0.5 }} />
          <Typography variant="caption">希望中</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 16, bgcolor: '#FFA726', borderRadius: 0.5 }} />
          <Typography variant="caption">承認待ち</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 16, bgcolor: '#66BB6A', borderRadius: 0.5 }} />
          <Typography variant="caption">承認済み</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 16, bgcolor: '#EF5350', borderRadius: 0.5 }} />
          <Typography variant="caption">却下</Typography>
        </Box>
      </Box>

      {/* コンテキストメニュー（シフト） */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem disabled>
          <ListItemText primary="スタッフ変更" />
        </MenuItem>
        {employees.map((employee) => {
          const shift = shifts.find((s) => s.id === contextMenu?.shiftId);
          const isCurrent = shift?.employeeId === employee.id;

          return (
            <MenuItem
              key={employee.id}
              onClick={() => handleChangeEmployee(employee.id)}
              selected={isCurrent}
            >
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={getEmployeeName(employee)} />
            </MenuItem>
          );
        })}
        <MenuItem divider />
        <MenuItem
          onClick={() => {
            if (contextMenu) {
              const shift = shifts.find((s) => s.id === contextMenu.shiftId);
              if (shift) {
                onShiftClick(shift);
              }
            }
            handleCloseContextMenu();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="詳細編集" />
        </MenuItem>
      </Menu>

      {/* コンテキストメニュー（募集枠） */}
      <Menu
        open={requestContextMenu !== null}
        onClose={() => setRequestContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          requestContextMenu !== null
            ? { top: requestContextMenu.mouseY, left: requestContextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem
          onClick={() => {
            if (requestContextMenu && onIncreaseRequest) {
              onIncreaseRequest(requestContextMenu.requestId);
            }
            setRequestContextMenu(null);
          }}
        >
          <ListItemIcon>
            <PlusOneIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="募集人数を増やす" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (requestContextMenu && onDeleteRequest) {
              onDeleteRequest(requestContextMenu.requestId);
            }
            setRequestContextMenu(null);
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="募集人数を減らす" />
        </MenuItem>
      </Menu>
    </Box>
  );
}
